import { Link } from "react-router-dom";
import type { RecordRow } from "./api";

export const CATEGORIES: { value: string; label: string; hint: string }[] = [
  { value: "quality_defect", label: "Quality defect", hint: "Part out of spec" },
  { value: "downtime", label: "Downtime", hint: "Line or machine stopped" },
  { value: "material_shortage", label: "Material shortage", hint: "Missing parts" },
  { value: "safety", label: "Safety", hint: "Hazard or near miss" },
  { value: "maintenance", label: "Maintenance", hint: "Equipment needs work" },
  { value: "improvement_idea", label: "Improvement idea", hint: "Make it better" },
  { value: "other", label: "Other", hint: "Anything else" },
];

export const ERP_ENTITIES: { value: string; label: string; placeholder: string }[] = [
  { value: "work_center", label: "Work center", placeholder: "LINE-3" },
  { value: "machine_center", label: "Machine center", placeholder: "PRESS-02" },
  { value: "production_order", label: "Production order", placeholder: "101022" },
  { value: "item", label: "Item", placeholder: "1000-BRKT" },
  { value: "lot", label: "Lot", placeholder: "L-2291" },
  { value: "vendor", label: "Vendor", placeholder: "V-10045" },
  { value: "customer", label: "Customer", placeholder: "C-20000" },
];

export const label = (s: string | null | undefined) =>
  !s ? "—" : s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Business process flows: the main path of each record type, D365-style.
export const FLOWS: Record<"issue" | "capa", { id: string; name: string }[]> = {
  issue: [
    { id: "new", name: "New" }, { id: "triaged", name: "Triaged" }, { id: "in_progress", name: "In progress" },
    { id: "resolved", name: "Resolved" }, { id: "closed", name: "Closed" },
  ],
  capa: [
    { id: "initiated", name: "Initiated" }, { id: "containment", name: "Containment" }, { id: "investigation", name: "Investigation" },
    { id: "action_plan", name: "Action plan" }, { id: "implementation", name: "Implementation" },
    { id: "effectiveness_check", name: "Effectiveness" }, { id: "closed", name: "Closed" },
  ],
};

const STATE_TONE: Record<string, string> = {
  new: "warn", triaged: "info", in_progress: "info", resolved: "ok", escalated: "ink", closed: "",
  initiated: "warn", containment: "danger", investigation: "info", action_plan: "info", implementation: "info",
  effectiveness_check: "info", cancelled: "",
  prospect: "warn", qualified: "info", customer: "ok", inactive: "",
};
export function StatePill({ state }: { state: string }) {
  return <span className={`status ${STATE_TONE[state] ?? ""}`}>{label(state)}</span>;
}
const SEV: Record<string, string> = { critical: "#8E192E", high: "#C4314B", medium: "#C19C00", low: "#5E6A78" };
export function Severity({ severity }: { severity?: string | null }) {
  if (!severity) return <span className="muted">—</span>;
  return <span className="sev" style={{ ["--sev" as any]: SEV[severity] }}>{label(severity)}</span>;
}

// Small inline stage indicator for grids.
export function MiniFlow({ type, state }: { type: string; state: string }) {
  const stages = FLOWS[type as "issue" | "capa"];
  if (!stages) return <span className="muted">—</span>;
  const i = stages.findIndex((s) => s.id === state);
  const at = state === "escalated" ? 1 : i;
  return (
    <span className="bpf-mini" style={{ width: 90 }} aria-label={`Stage: ${label(state)}`}>
      {stages.map((s, j) => <span key={s.id} className={j < at ? "done" : j === at ? "on" : ""} />)}
    </span>
  );
}

export function Mark({ size = 20, dark = false }: { size?: number; dark?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <polyline points="10,31 32,13 54,31" fill="none" stroke={dark ? "#FFFFFF" : "#1E2938"} strokeWidth="10" />
      <polyline points="10,45 32,27 54,45" fill="none" stroke={dark ? "#34C7D8" : "#0A7C8C"} strokeWidth="10" />
      <polyline points="10,59 32,41 54,59" fill="none" stroke="#0E96A8" strokeWidth="10" />
    </svg>
  );
}

export function ago(iso: string) {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Where a record opens in the app, by its type. */
export const recordHref = (type: string, n: string) =>
  type === "opportunity" ? `/opportunities/${n}` : type === "quote" ? `/quotes/${n}` : type === "account" ? `/accounts/${n}` : type === "eco" ? `/plm/changes/${n}`
  : type === "contract" ? `/contracts/${n}` : type === "work_order" ? `/maintenance/wo/${n}` : type === "asset" ? `/maintenance/assets/${n}` : type === "pm" ? `/maintenance/pm/${n}`
  : type === "lead" ? `/marketing/leads/${n}` : type === "campaign" ? `/marketing/campaigns/${n}` : type === "content" ? `/marketing/calendar?view=list`
  : type === "factory_request" ? `/factory/r/${n}` : type === "factory_task" ? `/factory/t/${n}` : type === "ticket" ? `/service-desk/${n}` : type === "count" ? `/inventory/counts/${n}`
  : type === "email" ? `/marketing/emails/${n}` : type === "sequence" ? `/marketing/sequences/${n}` : type === "page" ? `/marketing/pages/${n}` : type === "cpm_version" ? `/cpm/${n}`
  : type.startsWith("app:") ? `/m/${type.slice(4)}/${n}` : `/r/${n}`;
const TYPE_LABEL: Record<string, string> = { count: "Count", factory_request: "Request", factory_task: "Task", issue: "Issue", capa: "CAPA", lead: "Lead", campaign: "Campaign", content: "Content", account: "Account", opportunity: "Opportunity", quote: "Quote", contract: "Contract", eco: "Change order", work_order: "Work order", asset: "Asset", pm: "Preventive maintenance", ticket: "Ticket", email: "Email", sequence: "Sequence", page: "Landing page", cpm_version: "Budget" };
/** The record type a search names, if any: "issues" or "work order" → issue, work_order. */
export const typeNamed = (q: string) => {
  const t = q.trim().toLowerCase().replace(/s$/, "");
  return t.length < 3 ? null : Object.keys(TYPE_LABEL).find((k) => TYPE_LABEL[k].toLowerCase() === t) ?? null;
};
/** What a record is, in words: "Issue", "Work order". */
export const typeLabel = (type: string) => TYPE_LABEL[type] ?? label(type.replace(/^app:/, ""));

/** A list of records. `brief` (search, mixed types) shows only what every record has: number, title, type, status. */
export function RecordGrid({ rows, empty, showType = false, brief = false }: { rows: RecordRow[] | null; empty: string; showType?: boolean; brief?: boolean }) {
  if (!rows) return <div className="empty">Loading…</div>;
  if (!rows.length) return <div className="empty">{empty}</div>;
  return (
    <div className="table-wrap">
      <table className={`dgrid ${brief ? "rg-brief" : ""}`}>
        <thead>
          <tr>
            <th>Number</th><th>Title</th>{showType && <th>Type</th>}{!brief && <><th>Category</th><th>Severity</th><th>Stage</th></>}<th>Status</th><th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td><Link className="num" to={recordHref(r.type, r.number)}>{r.number}</Link></td>
              <td className="title" title={r.title}>{r.title}</td>
              {showType && <td>{typeLabel(r.type)}</td>}
              {!brief && <>
                <td>{label(r.category ?? r.source)}</td>
                <td><Severity severity={r.severity} /></td>
                <td><MiniFlow type={r.type} state={r.state} /></td>
              </>}
              <td><StatePill state={r.state} /></td>
              <td className="muted" style={{ whiteSpace: "nowrap" }}>{ago(r.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="msg error" role="alert">{error instanceof Error ? error.message : String(error)}</div>;
}
